import { useState } from "react";
import { MapPin } from "lucide-react";
import { proxyGoogleImage } from "../lib/googleImageProxy";

interface AgencyBranding {
  brandName: string;
  logoUrl?: string;
  primaryColor?: string;
  websiteUrl?: string;
}

interface LandingFooterProps {
  businessName: string;
  businessInfo?: any;
  theme: any;
  workspace?: any;
  location?: any;
  agencyBranding?: AgencyBranding;
}

const LandingFooter = ({
  businessName,
  businessInfo,
  theme,
  workspace,
  location,
  agencyBranding,
}: LandingFooterProps) => {
  const currentYear = new Date().getFullYear();
  const [logoError, setLogoError] = useState(false);

  // Get the address to display
  const address =
    location?.address ||
    businessInfo?.address ||
    businessInfo?.formatted_address;
  const mapsUrl = location?.maps_url;

  // Get logo URL - prioritize location media items, then workspace logo
  const logoUrl =
    location?.media_items?.logo?.sourceUrl ||
    location?.media_items?.logo?.thumbnailUrl ||
    location?.media_items?.logo?.googleUrl ||
    workspace?.logo_url;

  return (
    <footer
      className="py-8 border-t-2"
      style={{
        backgroundColor: "var(--bg-secondary)",
        borderTopColor: "var(--primary)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Thank you message */}
        <div className="text-center mb-6">
          <p className="text-gray-500 text-sm">Thank you for visiting us!</p>
        </div>

        {/* Business Name and Address - Center Aligned */}
        <div className="text-center mb-4">
          <div className="flex items-center justify-center mb-2">
            {logoUrl && !logoError ? (
              <img
                src={proxyGoogleImage(logoUrl, 200)}
                alt={businessName}
                className="h-8 w-auto mr-2 object-contain"
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                onError={() => {
                  // Fires only when the (already proxied) URL genuinely fails.
                  console.error("Footer logo failed to load after proxy:", logoUrl);
                  setLogoError(true);
                }}
              />
            ) : (
              logoError && (
                <div className="h-8 w-8 mr-2 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-sm font-bold text-gray-600">
                    {businessName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )
            )}
            <h3 className="font-bold text-lg">{businessName}</h3>
          </div>
          {address && (
            <div className="flex items-center justify-center text-sm text-gray-600">
              <span>{address}</span>
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 inline-flex items-center hover:text-blue-600 transition-colors"
                  aria-label="View on Google Maps"
                >
                  <MapPin className="h-3 w-3" />
                  <span className="sr-only">View on Google Maps</span>
                </a>
              )}
            </div>
          )}
        </div>

        {/* Copyright and Powered By */}
        <div className="pt-4 border-t text-center text-xs text-gray-600">
          {/* suppressHydrationWarning: currentYear comes from new Date().getFullYear(),
              which can differ between the SSR pod (cached HTML built in year N)
              and the browser hydrating in year N+1. Was the top source of
              FLY-LOCAL-BOOST-B (Hydration Error) on white-label subdomains. */}
          <p suppressHydrationWarning>
            © {currentYear} {businessName}. All rights reserved.
          </p>
          <p className="mt-1">
            Powered by{" "}
            {agencyBranding ? (
              <a
                href={agencyBranding.websiteUrl || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline font-medium"
                style={{ color: agencyBranding.primaryColor || "var(--primary)" }}
              >
                {agencyBranding.brandName}
              </a>
            ) : (
              <a
                href="https://fly-social.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: "var(--primary)" }}
              >
                Fly
              </a>
            )}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default LandingFooter;
